import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalQuery } from "../api/useGlobalQuery";
import type { ObserverInfo } from "../api/types";
import type { AuditFinding, AuditFindingsResponse } from "../api/types";
import { tryApiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import { formatDistanceBoth } from "../lib/units";
import { AUDIT_COLOR_INFO, AUDIT_LEVEL_ORDER, AUDIT_LEVEL_INFO, AUDIT_TABLE_BARS_HEADING, AUDIT_TABLE_CLEAR_FILTERS_LABEL, AUDIT_TABLE_FILTERS_BUTTON_LABEL, AUDIT_TABLE_FILTER_ADVERT_LABEL, AUDIT_TABLE_FILTER_CLOCK_LABEL, AUDIT_TABLE_FILTER_DUPLICATE_LABEL, AUDIT_TABLE_FILTER_LOOP_LABEL, AUDIT_TABLE_FILTER_FOOTPRINT_LABEL, AUDIT_TABLE_FILTER_MULTI_LABEL, AUDIT_TABLE_CLOCK_NOT_SET_TIP, AUDIT_TABLE_FILTER_PATH_LABEL, AUDIT_TABLE_FILTER_PKTS_LABEL, AUDIT_TABLE_CLOCK_NOT_REPORTED, AUDIT_TABLE_FILTER_REGION_LABEL, AUDIT_TABLE_HAS_SCOPE_GROUP_LABEL, AUDIT_TABLE_LOCATION_FILTER_ALL_LABEL, AUDIT_TABLE_LOCATION_FILTER_LABEL, AUDIT_TABLE_NEXT_LABEL, AUDIT_TABLE_NO_MATCH_LABEL, AUDIT_TABLE_NO_SCOPE_PILL_TIP, AUDIT_TABLE_NO_VALUE_LABEL, AUDIT_TABLE_PREV_LABEL, AUDIT_TABLE_ROLE_FILTER_ALL_LABEL, AUDIT_TABLE_ROLE_FILTER_LABEL, AUDIT_TABLE_SHOW_ALL_LABEL, AUDIT_TABLE_SORT_ASC_LABEL, AUDIT_TABLE_SORT_DEFAULT_LABEL, AUDIT_TABLE_SORT_DESC_LABEL, AUDIT_TABLE_SORT_LABEL, AUDIT_TABLE_TOP_SCOPE_PICKER_LABEL, auditPathHashWidthLabel, auditRoleLabel, auditTableAdvertHistorySentence, auditTableClockHistorySentence, auditTableClockStepLabel, auditTableClockStepTip, auditTableClockOffsetEvidenceSentence, auditTableConfirmedScopeLine, auditTableDuplicateAdjacencyConfirmedSentence, auditTableDuplicatePrefixSentence, auditTableGeoRegionEvidenceSentence, auditTableInferredScopeLine, auditTableLoopEvidenceSentence, auditTableNoScopeSentence, auditTablePagerLine, auditTablePathHashChangedSentence, auditTablePathHashChangedTip, auditTableDuplicateNoneSentence, AUDIT_TABLE_HEARD_TOGETHER_LABEL, AUDIT_TABLE_HEARD_TOGETHER_TIP, auditTablePathHashEvidenceSentence, auditTablePktsPerHrChannelMatchCaveat, auditTablePktsPerHrChannelMessagesSentence, auditTablePktsPerHrEvidenceSentence, auditTablePositionDisagreementIntro, auditTablePositionDisagreementPart, auditTableRelianceEvidenceSentence, auditTableScopePillTip, AUDIT_SCOPE_STALE_LABEL, auditTableScopeUnknownEvidenceSentence, auditTableWindowProseLabel, formatAdvertInterval, isAuditColor, type AuditColor } from '../lib/definitions/analysis';
import { STATUS, colorFor } from '../lib/definitions/palette';
import {
  PATH_FILTER_OPTIONS,
  MULTI_FILTER_OPTIONS,
  filterChips,
  activeFilterCount,
  cardFieldsFor,
  LOOP_UNKNOWN_TIP,
  advertNoColourTip,
  barSelection,
  isClockNotSet,
  clockCellForDisplay,
  clockStepLevel,
  issuePopulation,
  findingWorklistRows,
  observerDisplayNames,
  toggleBarFilter,
  DUP_FILTER_OPTIONS,
  LOOP_FILTER_OPTIONS,
  FOOTPRINT_FILTER_OPTIONS,
  FOOTPRINT_COLUMN_HEADINGS,
  footprintOf,
  scopeStaleLabel,
  footprintDeclaredText,
  footprintRelayedText,
  footprintOffShareText,
  footprintLabelText,
  footprintScopeTag,
  footprintReasonTag,
  SORT_COLUMNS,
  cardFields,
  parseSortChoice,
  sortChoiceValue,
  advertFilterOptions,
  auditCountLine,
  barCounts,
  heardTogetherText,
  heardTogetherTip,
  packetsByWidthText,
  packetsByWidthTip,
  widthSequenceText,
  type AuditPathHashPackets,
  barPopulation,
  dashTip,
  type DashColumn,
  barLabel,
  barRightText,
  barSegmentLabel,
  barTooltip,
  clockFilterOptions,
  columnLabel,
  defaultTopTag,
  headerTooltip,
  locationOptions,
  pktsFilterOptions,
  scopeFilterOptions,
  sortRows,
  visibleRows,
  type AuditCell,
  type AuditColorSettings,
  type AuditPayload,
  type AuditRow,
  type AuditScopePill,
  type AuditWindow,
  type BarId,
  type BarState,
  type ColumnFilterId,
  type ColumnFilters,
  type FilterOption,
  type SortKey,
} from "../lib/auditTable";
import { formatClockOffset, relativeTime } from "../lib/format";
import { localDayTime } from "../lib/paths";
import { packetTypeName, tzAbbrev } from "../lib/rfHealth";
import {
  AIRTIME_CAUSED_TIP,
  AIRTIME_NONE_REASON,
  AIRTIME_RELAYED_TIP,
  AIRTIME_SENT_TIP,
  AIRTIME_RANK_TIP,
  airtimeAdvertLine,
  AIRTIME_ADVERT_TIP,
  airtimeRankText,
  advertGapSentence,
  advertGapText,
  duplicateCellText,
  duplicateCellTip,
  airtimeTypeSplit,
  fmtSecPerDay,
} from "../lib/auditTable";
import { HoverTip } from "./HoverTip";
import { AuditHistorySelect, useAuditAsOf, useAuditHistory } from "./AuditHistory";
import { historyQuery } from "../lib/auditHistory";
import { useIsPhone } from "../lib/useIsPhone";
import { AUDIT_METRICS_VIEWS, readAuditMetricsView, writeAuditMetricsView, type AuditMetricsView } from "../lib/auditMetricView";
import { AuditGauges, AuditRings, type AuditMetricItem } from "./AuditMetricViews";

const PAGE_SIZE = 50;

type FindingLevelFilter = string | null;
type FindingLevelMembership = { byLevel: Record<string, Set<string>> };
type FindingLevelIndex = FindingLevelMembership & { window: AuditWindow; lineageKey: string; findingsSnapshotAt: string };
const UNKNOWN_FINDING_LEVEL = "unknown";

function normalizedFindingLevel(finding: AuditFinding): string {
  const level = finding.level;
  return level && (AUDIT_LEVEL_ORDER as string[]).includes(level) && level !== "not_enough_data"
    ? level
    : UNKNOWN_FINDING_LEVEL;
}

function findingLevelLabel(level: string): string {
  return level === UNKNOWN_FINDING_LEVEL
    ? "Unknown / insufficient evidence"
    : AUDIT_LEVEL_INFO[level as keyof typeof AUDIT_LEVEL_INFO].label;
}

function buildFindingLevelIndex(findings: AuditFinding[]): FindingLevelMembership {
  const byLevel: Record<string, Set<string>> = Object.fromEntries(
    [...AUDIT_LEVEL_ORDER.filter((level) => level !== "not_enough_data"), UNKNOWN_FINDING_LEVEL].map((level) => [level, new Set<string>()]),
  );
  for (const finding of findings) {
    const id = finding.subject?.native_id;
    if (!id) continue;
    const level = normalizedFindingLevel(finding);
    byLevel[level].add(id);
  }
  return { byLevel };
}

type Theme = "light" | "dark";

function swatch(color: string | null, effectiveTheme: Theme) {
  const info = color && isAuditColor(color) ? AUDIT_COLOR_INFO[color as AuditColor] : null;
  if (!info?.color) return null;
  return (
    <span
      className="legend-swatch"
      style={{ background: colorFor(info.color, effectiveTheme), display: "inline-block", marginRight: 4 }}
    />
  );
}

// A dash carries a tooltip saying why it is a dash, when the caller knows.
export function Dash({ tip }: { tip?: string }) {
  if (!tip) return <>{AUDIT_TABLE_NO_VALUE_LABEL}</>;
  return <HoverTip text={tip}><span className="audit-dash">{AUDIT_TABLE_NO_VALUE_LABEL}</span></HoverTip>;
}

/** Loop Detection and Advert Interval cells: the same cell, plus a tooltip on the two
 *  values that would otherwise be unexplained ("unknown"; no colour above the range). */
export function LoopCell({ cell, effectiveTheme, dash }: { cell: AuditCell; effectiveTheme: Theme; dash?: string }) {
  const view = <AuditCellView cell={cell} effectiveTheme={effectiveTheme} dash={dash} />;
  if (cell.value !== "unknown") return view;
  return <HoverTip text={LOOP_UNKNOWN_TIP}>{view}</HoverTip>;
}

export function AdvertCell({ cell, effectiveTheme, dash, settings }: { cell: AuditCell; effectiveTheme: Theme; dash?: string; settings: AuditColorSettings }) {
  const view = <AuditCellView cell={cell} effectiveTheme={effectiveTheme} dash={dash} />;
  const tip = advertNoColourTip(cell.value, settings);
  return tip ? <HoverTip text={tip}>{view}</HoverTip> : view;
}

export function AuditCellView({ cell, effectiveTheme, dash }: { cell: AuditCell; effectiveTheme: Theme; dash?: string }) {
  const text = cell.label ?? AUDIT_TABLE_NO_VALUE_LABEL;
  if (text === AUDIT_TABLE_NO_VALUE_LABEL) return <Dash tip={dash} />;
  return (
    <>
      {swatch(cell.color, effectiveTheme)}
      {text}
    </>
  );
}

/** App-format local time with the zone abbreviation, e.g. "Oct 5 15:04 MDT". */
export function changeTimeLabel(iso: string | null | undefined): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return "an unknown time";
  const when = localDayTime(iso);
  const tz = tzAbbrev(iso);
  return tz ? `${when} ${tz}` : when;
}

/** The Duplicate cell; when it names a nearest partner, a hover explains it. */
export function DuplicateCell({ row, effectiveTheme, dash }: { row: AuditRow; effectiveTheme: Theme; dash?: string }) {
  const { displaySettings } = useAppData();
  const view = <AuditCellView cell={row.duplicate} effectiveTheme={effectiveTheme} dash={dash} />;
  const km = row.duplicates_nearest_km;
  if (km == null) return view;
  // One short line ("Shared ×2 · 309 mi"); the other unit and the explanation sit in the hover.
  const both = formatDistanceBoth(km, displaySettings?.display.distance_unit);
  const count = typeof row.duplicate.value === "number" ? row.duplicate.value : null;
  return (
    <HoverTip text={duplicateCellTip(count, both, row.duplicate.label)}>
      <span>
        {swatch(row.duplicate.color, effectiveTheme)}
        {duplicateCellText(count, both, row.duplicate.label)}
      </span>
    </HoverTip>
  );
}

/** The Path Hash cell; a width change adds a hover with the sequence. */
export function PathHashCell({ row, effectiveTheme, dash }: { row: AuditRow; effectiveTheme: Theme; dash?: string }) {
  const view = <AuditCellView cell={row.path_hash} effectiveTheme={effectiveTheme} dash={dash} />;
  const ch = row.width_changes;
  if (!ch) return view;
  return (
    <HoverTip text={auditTablePathHashChangedTip(widthSequenceText(ch.widths), changeTimeLabel(ch.last_change))}>{view}</HoverTip>
  );
}

// Confirmed pills are solid, inferred pills dashed; red "No scope" is solid
// red. Colours are the app's shared STATUS tokens, never a second system.
export function ScopePill({ pill, effectiveTheme }: { pill: AuditScopePill; effectiveTheme: Theme }) {
  if (pill.color === "red") {
    const hex = colorFor(STATUS.critical, effectiveTheme);
    return (
      <HoverTip text={AUDIT_TABLE_NO_SCOPE_PILL_TIP}>
        <span className="audit-scope-pill" style={{ color: hex, borderColor: hex, background: `${hex}1a` }}>{pill.tag}</span>
      </HoverTip>
    );
  }
  const confirmed = pill.confirmed ?? !pill.dashed;
  const staleLabel = pill.stale ? scopeStaleLabel(pill.stale_days ?? 0) : null;
  const tip = auditTableScopePillTip(pill.tag, pill.name, confirmed, staleLabel);
  if (!confirmed) {
    return (
      <HoverTip text={tip}>
        <span className="audit-scope-pill audit-scope-pill-grey">{pill.tag}</span>
      </HoverTip>
    );
  }
  // A stale scope must not look healthy: the existing warning (amber) status colour instead of the green one.
  const hex = colorFor(pill.stale ? STATUS.warning : STATUS.good, effectiveTheme);
  // The warning text/border use the themed --rf-warn token (readable on light and dark); the tint is the warning status colour.
  const pillStyle = pill.stale
    ? { color: "var(--rf-warn)", borderColor: "var(--rf-warn)", background: `${hex}1a` }
    : { color: hex, borderColor: hex, background: `${hex}1a` };
  return (
    <HoverTip text={tip}>
      <span className="audit-scope-pill" style={pillStyle}>
        {pill.tag}
        {pill.stale && <span className="audit-scope-stale">{AUDIT_SCOPE_STALE_LABEL}</span>}
      </span>
    </HoverTip>
  );
}

function RegionPills({ row, effectiveTheme }: { row: AuditRow; effectiveTheme: Theme }) {
  const pills = row.region.scope_pills;
  if (!pills || pills.length === 0) return <Dash tip={dashTip(row, "region")} />;
  // On a phone only the first pill shows, then "+N" (its tooltip lists the rest).
  const rest = pills.slice(1);
  return (
    <>
      <ScopePill pill={pills[0]} effectiveTheme={effectiveTheme} />
      {rest.length > 0 && (
        <span className="audit-pills-rest">
          {rest.map((p, i) => <ScopePill key={`${p.tag}-${i}`} pill={p} effectiveTheme={effectiveTheme} />)}
        </span>
      )}
      {rest.length > 0 && (
        <HoverTip text={rest.map((p) => p.tag).join(", ")} className="audit-pills-more">
          <span className="audit-scope-pill audit-scope-pill-grey">+{rest.length}</span>
        </HoverTip>
      )}
    </>
  );
}

export function relianceValue(row: AuditRow): number | null {
  const infra = row.role === "repeater" || row.role === "room_server";
  return infra ? row.reliance.value : null;
}

function ClockCell({ row, effectiveTheme }: { row: AuditRow; effectiveTheme: Theme }) {
  const cell = <AuditCellView cell={clockCellForDisplay(row.clock_offset)} effectiveTheme={effectiveTheme} dash={dashTip(row, "clock_offset")} />;
  if (isClockNotSet(row.clock_offset.value)) return <HoverTip text={AUDIT_TABLE_CLOCK_NOT_SET_TIP}>{cell}</HoverTip>;
  const level = clockStepLevel(row.clock_offset);
  if (level !== null && level >= 1) return <HoverTip text={auditTableClockStepTip(level)}>{cell}</HoverTip>;
  return cell;
}

function RelianceCell({ row }: { row: AuditRow }) {
  const v = relianceValue(row);
  if (v === null) return <span className="settings-field-desc"><Dash tip={dashTip(row, "reliance")} /></span>;
  return (
    <HoverTip placement="above" text={auditTableRelianceEvidenceSentence(row.reliance.choke, row.reliance.through, v)}>
      <span>
        <span className="audit-reliance-bar"><i style={{ width: `${Math.min(100, v)}%` }} /></span>{" "}
        {Math.round(v)}% · {row.reliance.through.toLocaleString("en-US")}
      </span>
    </HoverTip>
  );
}

// A leading pictographic glyph some operators put in a node's own name
// renders as a tofu box in this app's font stack; stripped only for the
// Duplicate evidence's inline links, where a broken glyph is most visible.
function stripLeadingGlyph(label: string): string {
  return label.replace(/^\p{Extended_Pictographic}️?\s*/u, "");
}

function NodeLink({ nativeId, label }: { nativeId: string; label?: string | null }) {
  const text = label ? stripLeadingGlyph(label) : "";
  return <a href={`#/nodes/${encodeURIComponent(nativeId)}`}>{text || nativeId}</a>;
}

/** Every other repeater or room server sharing this node's short ID at its own width. */
export function DuplicatePartners({ row }: { row: AuditRow }) {
  const { displaySettings } = useAppData();
  const partners = row.duplicates ?? [];
  if (row.path_width == null) return null;
  if (partners.length === 0) return <div>{auditTableDuplicateNoneSentence(row.path_width)}</div>;
  return (
    <div>
      {partners.map((p) => (
        <div key={p.node_id}>
          <NodeLink nativeId={p.node_id} label={p.name} />
          {" · "}{formatDistanceBoth(p.km, displaySettings?.display.distance_unit)}
          {" · "}<HoverTip text={AUDIT_TABLE_HEARD_TOGETHER_TIP}><span>{AUDIT_TABLE_HEARD_TOGETHER_LABEL}</span></HoverTip>: <HoverTip text={heardTogetherTip(p)}><span>{heardTogetherText(p)}</span></HoverTip>
        </div>
      ))}
    </div>
  );
}

function DuplicateEvidence({ row }: { row: AuditRow }) {
  const memberships = (row.duplicate.evidence?.memberships as Array<Record<string, unknown>>) || [];
  if (memberships.length === 0 && row.path_width == null) return null;
  return (
    <div style={{ marginBottom: 6 }}>
      <strong>{columnLabel("duplicate")}</strong>
      <div>
        {memberships.map((m, i) => (
          <div key={i}>
            {auditTableDuplicatePrefixSentence(m.width_bytes as number, m.prefix as string, m.other_count as number)}
            {m.adjacency_confirmed ? ` ${auditTableDuplicateAdjacencyConfirmedSentence()}` : ""}
          </div>
        ))}
        <DuplicatePartners row={row} />
      </div>
    </div>
  );
}

function observerLabelFor(observers: ObserverInfo[], id: string): string {
  return observerDisplayNames(observers)[id] ?? observers.find((o) => o.id === id)?.label ?? id;
}

// Airtime block: seconds on air per day for what the node sent, what it
// relayed for others, and what its own packets cost the whole mesh. Shown in
// the Audit row's opened evidence and on the node page.
function AirtimeRank({ b }: { b: { rank?: number | null; of?: number | null } }) {
  const text = airtimeRankText(b);
  if (!text) return null;
  return <> · <HoverTip placement="above" text={AIRTIME_RANK_TIP}><span>{text}</span></HoverTip></>;
}

export function AirtimeBlock({ row, windowLabel }: { row: AuditRow; windowLabel: string }) {
  const a = row.airtime;
  if (!a) {
    return <div className="audit-airtime"><Dash tip={AIRTIME_NONE_REASON} /> <span className="settings-field-desc">{AIRTIME_NONE_REASON}</span></div>;
  }
  const gapLabel = row.advert_interval.value != null ? row.advert_interval.label : null;
  const advertLine = airtimeAdvertLine(a.advert, gapLabel ? advertGapText(gapLabel, row.advert_interval.evidence?.sample_count as number | null | undefined) : null, windowLabel);
  const split = (by: typeof a.sent.by_type) => (by.length > 0 ? ` (${airtimeTypeSplit(by, packetTypeName)})` : "");
  return (
    <div className="audit-airtime">
      <div>
        <HoverTip placement="above" text={AIRTIME_SENT_TIP}><span>Sent by this node</span></HoverTip>: {fmtSecPerDay(a.sent.s_per_day)} s/day<AirtimeRank b={a.sent} />{split(a.sent.by_type)}
      </div>
      <div>
        <HoverTip placement="above" text={AIRTIME_RELAYED_TIP}><span>Relayed for others</span></HoverTip>: {fmtSecPerDay(a.relayed.s_per_day)} s/day<AirtimeRank b={a.relayed} />
      </div>
      <div>
        <HoverTip placement="above" text={AIRTIME_CAUSED_TIP}><span>Caused across the mesh</span></HoverTip>: {fmtSecPerDay(a.caused.s_per_day)} s/day<AirtimeRank b={a.caused} />{split(a.caused.by_type)}
      </div>
      {advertLine && <div><HoverTip placement="above" text={AIRTIME_ADVERT_TIP}><span>{advertLine}</span></HoverTip></div>}
      <div className="settings-field-desc">Seconds per day, averaged over the last {windowLabel}.</div>
    </div>
  );
}

/** Scope Footprint detail: verdict with declared/relayed, then Scope / Packets / Days / Declared. */
export function FootprintBlock({ row, effectiveTheme }: { row: AuditRow; effectiveTheme: Theme }) {
  const fp = footprintOf(row);
  if (!fp || !row.footprint) return null;
  const off = footprintOffShareText(fp);
  return (
    <div className="audit-footprint">
      <div>
        {footprintLabelText(row)}
        {footprintReasonTag(fp) && <> <span className="settings-field-desc">{footprintReasonTag(fp)}</span></>}
        {row.footprint.value !== "unknown" && <>{" "}<AuditCellView cell={row.footprint} effectiveTheme={effectiveTheme} /></>}
      </div>
      <div>Declared: {footprintDeclaredText(fp)}</div>
      <div>Relayed: {footprintRelayedText(fp)}</div>
      {fp.scopes.length > 0 && (
        <table className="audit-footprint-table">
          <thead><tr>{FOOTPRINT_COLUMN_HEADINGS.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {fp.scopes.map((sc) => (
              <tr key={sc.scope}>
                <td>{sc.scope}</td><td>{sc.packets}</td><td>{sc.days}</td><td>{sc.declared ? (fp.stale_declared?.[sc.scope] ? `Yes (${scopeStaleLabel(fp.stale_declared[sc.scope].stale_days)})` : "Yes") : "No"}{footprintScopeTag(sc) && <> <span className="settings-field-desc">{footprintScopeTag(sc)}</span></>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {off && <div>{off}</div>}
    </div>
  );
}

// Plain-sentence evidence per column, shown when a row is opened. Only
// renders a section when that column actually has something to show.
export function EvidenceBlock({
  row, windowKey, observers, widthLookbackDays, effectiveTheme,
}: { row: AuditRow; windowKey: AuditWindow; observers: ObserverInfo[]; widthLookbackDays: number; effectiveTheme: Theme }) {
  const windowProse = auditTableWindowProseLabel(windowKey);
  const asOf = useAuditAsOf();
  const ev = row.region.evidence ?? {};
  const confirmedScopes = (ev.confirmed_scopes as string[]) || [];
  const inferredScopes = (ev.inferred_scopes as string[]) || [];
  const staleScopes = (ev.stale_scopes as Record<string, { stale_days: number }>) || {};
  const pillByTag = new Map((row.region.scope_pills ?? []).map((p) => [p.tag, p]));
  const geo = (ev.geo as {
    region_name: string | null; has_position?: boolean; position_disagreements?: Array<Record<string, unknown>>;
  }) || { region_name: null };
  const positionDisagreements = geo.position_disagreements || [];

  const clockHistory = (row.clock_offset.evidence?.history as Array<{ offset_s: number; at: string }>) || [];
  const clockSteps = (row.clock_offset.evidence?.steps as Array<{ at: string; kind: string; k: number | null }>) || [];
  const advertHistory = (row.advert_interval.evidence?.recent_intervals_hours as number[]) || [];
  const channelMessagesMatched = row.pkts_per_hr.evidence?.channel_messages_matched as number | null | undefined;
  const channelMessagesByChannel = (row.pkts_per_hr.evidence?.channel_messages_by_channel as Array<{
    channel: string; count: number;
  }>) || [];
  const proof = (row.path_hash.evidence?.multibyte_proof as "advert" | "relayed" | null | undefined) ?? null;
  const loopPriors = (row.loop_detection.evidence?.max_prior_occurrences_by_width as Record<string, number>) || {};
  const isInfra = row.role === "repeater" || row.role === "room_server";

  return (
    <div className="node-detail-note audit-evidence">
      <div style={{ marginBottom: 6 }}>
        <strong>{columnLabel("region")}</strong>
        <div>
          {row.region.scope_pills !== null && (
            <>
              {confirmedScopes.map((t, i) => (
                <div key={`c-${i}`}>{auditTableConfirmedScopeLine({ tag: t, name: pillByTag.get(t)?.name ?? null }, staleScopes[t] ? scopeStaleLabel(staleScopes[t].stale_days) : null)}</div>
              ))}
              {inferredScopes.map((t, i) => (
                <div key={`i-${i}`}>{auditTableInferredScopeLine({ tag: t, name: pillByTag.get(t)?.name ?? null })}</div>
              ))}
              {row.region.scope_state === "no_scope" && (
                <div>{auditTableNoScopeSentence((ev.unscoped_packets as number) || 0)}</div>
              )}
              {row.region.scope_state === "unknown" && <div>{auditTableScopeUnknownEvidenceSentence()}</div>}
            </>
          )}
          <div>{auditTableGeoRegionEvidenceSentence(geo.region_name, !!geo.has_position)}</div>
          {positionDisagreements.length > 0 && (
            <div>
              {auditTablePositionDisagreementIntro()}{" "}
              {positionDisagreements.map((d, i) => (
                <span key={i}>
                  {i > 0 ? "; " : ""}
                  {((d.values as Array<{ observer_id: string; value: { lat: number; lon: number } | null }>) || []).map((v, j) => (
                    v.value ? (
                      <span key={j}>
                        {j > 0 ? "; " : ""}
                        {auditTablePositionDisagreementPart(observerLabelFor(observers, v.observer_id), v.value.lat, v.value.lon)}
                      </span>
                    ) : null
                  ))}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {row.path_width != null && (
        <div style={{ marginBottom: 6 }}>
          <strong>{columnLabel("path_hash")}</strong>
          <div>
            {auditTablePathHashEvidenceSentence(auditPathHashWidthLabel(row.path_width), widthLookbackDays, proof)}
            {row.width_changes && (
              <>{" "}{auditTablePathHashChangedSentence(widthSequenceText(row.width_changes.widths), changeTimeLabel(row.width_changes.last_change))}</>
            )}
          </div>
        </div>
      )}

      {isInfra && <DuplicateEvidence row={row} />}

      {(
        <div style={{ marginBottom: 6 }}>
          <strong>{columnLabel("clock_offset")}</strong>
          <div>
            {row.clock_offset.value == null ? <div>{AUDIT_TABLE_CLOCK_NOT_REPORTED}</div> : <div>
              {isClockNotSet(row.clock_offset.value)
                ? AUDIT_TABLE_CLOCK_NOT_SET_TIP
                : auditTableClockOffsetEvidenceSentence(row.clock_offset.label || "", row.clock_offset.evidence?.drift_s_per_day as number | null, clockStepLevel(row.clock_offset))}
            </div>}
            {(clockHistory.length > 1 || clockSteps.length > 0) && (
              <div>{auditTableClockHistorySentence(
                [
                  ...clockHistory.map((h) => ({ at: h.at, text: `${formatClockOffset(h.offset_s)} (${relativeTime(h.at, asOf)})` })),
                  ...clockSteps.map((st) => ({ at: st.at, text: `${auditTableClockStepLabel(st.kind)} (${relativeTime(st.at, asOf)})` })),
                ].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0)).map((p) => p.text),
              )}</div>
            )}
          </div>
        </div>
      )}

      {row.advert_interval.value != null && (
        <div style={{ marginBottom: 6 }}>
          <strong>{columnLabel("advert_interval")}</strong>
          <div>
            <div>{advertGapSentence(row.advert_interval.label || "", row.advert_interval.evidence?.sample_count as number | null)}</div>
            {advertHistory.length > 1 && (
              <div>{auditTableAdvertHistorySentence(advertHistory.map((h) => formatAdvertInterval(h)))}</div>
            )}
          </div>
        </div>
      )}

      {isInfra && footprintOf(row) && (
        <div style={{ marginBottom: 6 }}>
          <strong>{columnLabel("footprint")}</strong>
          <FootprintBlock row={row} effectiveTheme={effectiveTheme} />
        </div>
      )}

      {isInfra && (
        <div style={{ marginBottom: 6 }}>
          <strong>{columnLabel("loop_detection")}</strong>
          <div>
            {auditTableLoopEvidenceSentence(String(row.loop_detection.value ?? "unknown"), loopPriors)}
          </div>
        </div>
      )}

      {row.pkts_per_hr.value != null && (
        <div style={{ marginBottom: 6 }}>
          <strong>{columnLabel("pkts_per_hr")}</strong>
          <div>
            <div>{auditTablePktsPerHrEvidenceSentence(row.pkts_per_hr.label || "", windowProse)}</div>
            {channelMessagesMatched != null && channelMessagesMatched > 0 && (
              <>
                <div>{auditTablePktsPerHrChannelMessagesSentence(channelMessagesMatched, channelMessagesByChannel)}</div>
                <div>{auditTablePktsPerHrChannelMatchCaveat()}</div>
              </>
            )}
          </div>
        </div>
      )}
      {isInfra && (
        <div style={{ marginBottom: 6 }}>
          <strong>{columnLabel("reliance")}</strong>
          <div>
            {auditTableRelianceEvidenceSentence(row.reliance.choke, row.reliance.through, row.reliance.value)}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 6 }}>
        <strong>Airtime</strong>
        <div>
          <AirtimeBlock row={row} windowLabel={windowProse} />
        </div>
      </div>

    </div>
  );
}

// One summary bar: label left, "x% (n of N, u unknown)" right, a thin
// segmented bar below (green yes / hatched unknown / red no). The whole row
// carries the hover explanation; each segment filters the table.
function SummaryBar({
  bar, label, counts, tooltip, selected, onPick, effectiveTheme, packets, filtered,
}: {
  bar: BarId; label: React.ReactNode; counts: ReturnType<typeof barCounts>; tooltip: string;
  packets?: AuditPathHashPackets;
  filtered?: boolean;
  selected: BarState | null; onPick: (s: BarState) => void; effectiveTheme: Theme;
}) {
  const segs: { state: BarState; n: number; style: React.CSSProperties; cls: string }[] = [
    { state: "yes", n: counts.yes, style: { background: colorFor(STATUS.good, effectiveTheme) }, cls: "" },
    ...(bar === "clock" ? [{ state: "mid" as BarState, n: counts.mid, style: { background: colorFor(STATUS.warning, effectiveTheme) }, cls: "" }] : []),
    { state: "unknown", n: counts.unknown, style: {}, cls: " audit-bar-seg-unknown" },
    { state: "no", n: counts.no, style: { background: colorFor(STATUS.critical, effectiveTheme) }, cls: "" },
  ];
  const packetsText = bar === "path" ? packetsByWidthText(packets) : null;
  return (
    <div className="audit-bar-row">
      <HoverTip text={tooltip} block>
        <div className="audit-bar-top">
          <span>{label}</span>
          <span className="settings-field-desc">{barRightText(counts, bar)}</span>
        </div>
      </HoverTip>
      {packetsText && packets && (
        <div className={`audit-bar-packets${filtered ? " settings-field-desc" : ""}`}>
          <HoverTip block text={packetsByWidthTip(packets)}><span>{packetsText}</span></HoverTip>
        </div>
      )}
      <div className="audit-bar">
        {segs.filter((s) => s.n > 0).map((s) => (
          <button
            key={s.state}
            type="button"
            className={`audit-bar-seg${s.cls}${selected === s.state ? " audit-bar-seg-selected" : ""}${selected !== null && selected !== s.state ? " audit-bar-seg-dim" : ""}`}
            style={{ ...s.style, width: `${(s.n * 100) / Math.max(1, counts.total)}%` }}
            aria-label={`${bar} ${barSegmentLabel(s.state)} ${s.n}`}
            aria-pressed={selected === s.state}
            onClick={() => onPick(s.state)}
          />
        ))}
      </div>
    </div>
  );
}

function FilterSelect({
  id, label, value, onChange, children,
}: { id: string; label: string; value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <span className="audit-filter">
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>{children}</select>
    </span>
  );
}

function Options({ options }: { options: FilterOption[] }) {
  return <>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</>;
}

/** One non-Node column's cell content. The table and the phone card both render through this,
 *  so wording, colours and hover text cannot drift apart. */
function AuditFieldValue({ colKey, row, effectiveTheme, settings }: { colKey: SortKey; row: AuditRow; effectiveTheme: Theme; settings: AuditColorSettings }) {
  const asOf = useAuditAsOf();
  switch (colKey) {
    case "role": return <>{auditRoleLabel(row.role)}</>;
    case "region": return <RegionPills row={row} effectiveTheme={effectiveTheme} />;
    case "footprint": {
      if (!row.footprint) return <Dash tip={dashTip(row, "footprint")} />;
      const fp = footprintOf(row);
      const reason = fp ? footprintReasonTag(fp) : null;
      return (
        <>
          <AuditCellView cell={row.footprint} effectiveTheme={effectiveTheme} dash={dashTip(row, "footprint")} />
          {reason && <> <span className="audit-footprint-reason settings-field-desc">{reason}</span></>}
        </>
      );
    }
    case "path_hash": return <PathHashCell row={row} effectiveTheme={effectiveTheme} dash={dashTip(row, "path_hash")} />;
    case "duplicate": return <DuplicateCell row={row} effectiveTheme={effectiveTheme} dash={dashTip(row, "duplicate")} />;
    case "clock_offset": return <ClockCell row={row} effectiveTheme={effectiveTheme} />;
    case "advert_interval": return <AdvertCell cell={row.advert_interval} effectiveTheme={effectiveTheme} dash={dashTip(row, "advert_interval")} settings={settings} />;
    case "loop_detection": return <LoopCell cell={row.loop_detection} effectiveTheme={effectiveTheme} dash={dashTip(row, "loop_detection")} />;
    case "last_heard": return <>{row.last_heard ? relativeTime(row.last_heard, asOf) : <Dash tip={dashTip(row, "last_heard")} />}</>;
    case "pkts_per_hr": return <AuditCellView cell={row.pkts_per_hr} effectiveTheme={effectiveTheme} dash={dashTip(row, "pkts_per_hr")} />;
    case "reliance": return <RelianceCell row={row} />;
    default: return null;
  }
}

function AuditCard({
  row, effectiveTheme, settings, expanded, onToggle, windowKey, observers, widthLookbackDays,
}: {
  row: AuditRow; effectiveTheme: Theme; settings: AuditColorSettings; expanded: boolean; onToggle: () => void;
  windowKey: AuditWindow; observers: ObserverInfo[]; widthLookbackDays: number;
}) {
  return (
    <div className="audit-card" onClick={onToggle}>
      <div className="audit-card-top">
        <a href={`#/nodes/${encodeURIComponent(row.native_id)}`} className={`node-name-link${/\S{24,}/.test(row.name) ? " audit-name-unbroken" : ""}`} onClick={(e) => e.stopPropagation()}>
          {row.name}
        </a>
        <div className="behaviour-node-short-id settings-field-desc">
          {row.short_id}{row.region.label ? ` · ${row.region.label}` : ""}
        </div>
      </div>
      <div className="audit-card-fields">
        {cardFieldsFor(row).map((f) => (
          <div className="audit-card-line" key={f.key}>
            <span className="audit-card-label">{f.label}</span>
            <span className="audit-card-value"><AuditFieldValue colKey={f.key} row={row} effectiveTheme={effectiveTheme} settings={settings} /></span>
          </div>
        ))}
      </div>
      {expanded && (
        <div className="audit-expand-panel" onClick={(e) => e.stopPropagation()}>
          <EvidenceBlock row={row} windowKey={windowKey} observers={observers} widthLookbackDays={widthLookbackDays} effectiveTheme={effectiveTheme} />
        </div>
      )}
    </div>
  );
}

export function AuditTable() {
  const { effectiveTheme, observers } = useAppData();
  const [windowChoice, setWindowKey] = useState<AuditWindow>("7d");
  // While a History snapshot is chosen the table shows that snapshot's 7-day result.
  const { takenAt } = useAuditHistory();
  const windowKey: AuditWindow = takenAt ? "7d" : windowChoice;
  const [showAll, setShowAll] = useState(false);
  const [location, setLocation] = useState("");
  const [role, setRole] = useState("");
  const [filters, setFilters] = useState<ColumnFilters>({});
  const [topPick, setTopPick] = useState<string | null>(null);
  // null = the default order: issues first (red, then amber), then name.
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [findingLevel, setFindingLevel] = useState<FindingLevelFilter>(null);
  const [findingLevels, setFindingLevels] = useState<FindingLevelIndex | null>(null);
  const [findingLevelsUnavailableKey, setFindingLevelsUnavailableKey] = useState<string | null>(null);
  const findingLevelsCache = useRef(new Map<string, FindingLevelIndex>());
  const [findingRefreshNonce, setFindingRefreshNonce] = useState(0);
  const isPhone = useIsPhone();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Persisted (IndexedDB) TanStack Query cache, one entry per window: the
  // last-known table shows instantly on reload while a background refetch
  // keeps it fresh. The FULL row set is fetched once per window; every
  // filter, bar count and sort happens client-side over it.
  const { data, loading, everSucceeded } = useGlobalQuery<AuditPayload>(
    takenAt ? ["auditTable", windowKey, takenAt] : ["auditTable", windowKey],
    `/api/audit/table?window=${windowKey}&issues_only=false${historyQuery(takenAt)}`,
    120000,
    true,
    true,
  );

  // The worklist uses the Audit findings endpoint's existing levels. Fetch
  // every cursor page once per table refresh so each chip counts distinct
  // nodes over the whole window, not just the first findings page.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const tableSnapshot = data?.generated_at;
    const findingsSnapshotAt = data?.findings_snapshot_at;
    const lineageKey = `${windowKey}:${tableSnapshot ?? ""}:${findingsSnapshotAt ?? ""}:${data?.window_start ?? ""}:${data?.window_end ?? ""}`;
    if (!tableSnapshot) return () => { cancelled = true; controller.abort(); };
    setFindingLevels(null);
    setFindingLevelsUnavailableKey(null);
    if (!findingsSnapshotAt) {
      return () => { cancelled = true; controller.abort(); };
    }
    const cacheKey = lineageKey;
    const cachedLevels = findingLevelsCache.current.get(cacheKey);
    if (cachedLevels) {
      setFindingLevels(cachedLevels);
      setFindingLevelsUnavailableKey(null);
      return () => { cancelled = true; controller.abort(); };
    }
    void (async () => {
      try {
        const findings: AuditFinding[] = [];
        const cursors = new Set<string>();
        let cursor: string | null = null;
        let snapshot: Pick<AuditFindingsResponse, "generated_at" | "window_start" | "window_end"> | null = null;
        let pageCount = 0;
        while (true) {
          if (++pageCount > 200) throw new Error("Audit findings exceeded the page limit");
          const params = new URLSearchParams({ window: windowKey, protocol: "meshcore", limit: "500" });
          if (takenAt) params.set("taken_at", takenAt);
          if (cursor) params.set("cursor", cursor);
          const response = await tryApiGet<AuditFindingsResponse>(`/api/audit/findings?${params.toString()}`, controller.signal);
          if (!response) throw new Error("Audit findings are unavailable");
          if (!snapshot && response.generated_at !== findingsSnapshotAt) {
            throw new Error("Audit findings do not match the Audit table snapshot lineage");
          }
          if (snapshot && (response.generated_at !== snapshot.generated_at || response.window_start !== snapshot.window_start || response.window_end !== snapshot.window_end)) {
            throw new Error("Audit findings changed while paging");
          }
          snapshot ??= { generated_at: response.generated_at, window_start: response.window_start, window_end: response.window_end };
          findings.push(...response.findings);
          const next = response.next_cursor ?? null;
          if (!next) {
            if (response.total != null && findings.length < response.total) throw new Error("Audit findings response is incomplete");
            break;
          }
          if (cursors.has(next)) throw new Error("Audit findings cursor repeated");
          cursors.add(next);
          cursor = next;
        }
        if (!cancelled && snapshot) {
          const index = {
            ...buildFindingLevelIndex(findings),
            window: windowKey,
            lineageKey,
            findingsSnapshotAt: snapshot.generated_at,
          };
          for (const key of findingLevelsCache.current.keys()) {
            if (key.startsWith(`${windowKey}:`)) findingLevelsCache.current.delete(key);
          }
          findingLevelsCache.current.set(lineageKey, index);
          setFindingLevels(index);
        }
      } catch {
        if (!cancelled) setFindingLevelsUnavailableKey(`${lineageKey}:${findingRefreshNonce}`);
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [windowKey, takenAt, data?.generated_at, data?.findings_snapshot_at, data?.window_start, data?.window_end, findingRefreshNonce]);

  const rows = useMemo(() => (data && !data.pending ? data.rows : []), [data]);
  const settings = data?.color_settings;
  const top = data?.top_level_region_scope ?? null;
  const tokens = useMemo(() => data?.scope_tokens ?? [], [data]);
  const topTag = topPick && tokens.includes(topPick) ? topPick : defaultTopTag(top, tokens, rows);

  const locations = useMemo(() => locationOptions(rows), [rows]);
  const roles = useMemo(() => Array.from(new Set(rows.map((r) => r.role))).sort(), [rows]);
  const population = useMemo(() => issuePopulation(rows, location, role, showAll), [rows, location, role, showAll]);
  // The summary bars count exactly the repeaters and room servers the table
  // would list under every current filter, so a clicked segment always
  // yields that segment's count of rows.
  // All four bars recount against the same filtered set, the clicked bar too.
  const barRows = useMemo(() => {
    const out = {} as Record<BarId, AuditRow[]>;
    for (const b of ["path", "scopes", "multibyte", "top", "clock"] as BarId[]) {
      out[b] = settings ? barPopulation(rows, location, role, filters, { settings }, topTag) : [];
    }
    return out;
  }, [rows, location, role, filters, settings, topTag]);
  const levelCandidates = useMemo(() => {
    if (!settings) return [];
    return sortRows(findingWorklistRows(rows, { location, role, filters, showAll }, { settings }, topTag), sortKey, sortDir);
  }, [rows, settings, location, role, filters, showAll, topTag, sortKey, sortDir]);
  const shownWithoutLevel = useMemo(() => {
    if (!settings) return [];
    const list = visibleRows(rows, { location, role, filters, showAll }, { settings }, topTag);
    return sortRows(list, sortKey, sortDir);
  }, [rows, settings, location, role, filters, showAll, topTag, sortKey, sortDir]);
  const lineageKey = `${windowKey}:${data?.generated_at ?? ""}:${data?.findings_snapshot_at ?? ""}:${data?.window_start ?? ""}:${data?.window_end ?? ""}`;
  const findingsLineageMissing = !!data?.generated_at && !data.findings_snapshot_at;
  const currentFindingLevels = findingLevels?.window === windowKey && findingLevels.lineageKey === lineageKey && findingLevels.findingsSnapshotAt === data?.findings_snapshot_at ? findingLevels : null;
  const worklistUnavailable = findingLevelsUnavailableKey === `${lineageKey}:${findingRefreshNonce}`;
  const levelWorklistRows = useMemo(() => {
    if (!currentFindingLevels) return [];
    return levelCandidates.filter((row) => currentFindingLevels.byLevel[findingLevel ?? ""]?.has(row.native_id));
  }, [levelCandidates, currentFindingLevels, findingLevel]);
  const shown = findingLevel ? (currentFindingLevels ? levelWorklistRows : []) : shownWithoutLevel;
  const levelCounts = useMemo(() => {
    if (!currentFindingLevels) return null;
    const counts: Record<string, number> = {};
    for (const level of [...AUDIT_LEVEL_ORDER.filter((item) => item !== "not_enough_data"), UNKNOWN_FINDING_LEVEL]) {
      const nodes = currentFindingLevels.byLevel[level];
      counts[level] = levelCandidates.reduce((n, row) => n + (nodes?.has(row.native_id) ? 1 : 0), 0);
    }
    return counts;
  }, [currentFindingLevels, levelCandidates]);

  useEffect(() => { setPage(0); }, [location, role, filters, showAll, sortKey, sortDir, windowKey, findingLevel]);
  useEffect(() => { setFindingLevel(null); }, [windowKey]);

  // Right-edge fade on the table's scroll box: shown only while there is more to the right.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [moreRight, setMoreRight] = useState(false);
  const [panelW, setPanelW] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
      setPanelW(el.clientWidth);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); };
  });

  const [metricsView, setMetricsView] = useState<AuditMetricsView>(() => readAuditMetricsView());

  if (!everSucceeded && loading) return <div className="node-detail-note">Loading…</div>;
  if (data?.pending) return <div className="node-detail-note">{data.message}</div>;
  if (!settings) return <div className="node-detail-note">Loading…</div>;

  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const pageIndex = Math.min(page, pages - 1);
  const slice = shown.slice(pageIndex * PAGE_SIZE, pageIndex * PAGE_SIZE + PAGE_SIZE);
  const issues = population.filter((r) => r.has_issue).length;
  const filteredNow = Object.values(filters).some(Boolean);
  const scopeOpts = scopeFilterOptions(topTag, tokens);
  const lookbackDays = data?.fixed_lookback_days ?? null;

  function setFilter(id: ColumnFilterId, v: string) {
    setFilters((f) => ({ ...f, [id]: v }));
  }

  function chooseMetricsView(v: AuditMetricsView) {
    setMetricsView(v);
    writeAuditMetricsView(v);
  }

  function pickSegment(bar: BarId, state: BarState) {
    setFilters((f) => toggleBarFilter(f, bar, state));
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDir(1); } // first click: worst first on a coloured column
  }

  const bars: { id: BarId; label: React.ReactNode; labelText: string; picker?: React.ReactNode }[] = [
    { id: "path", label: barLabel("path", null), labelText: barLabel("path", null) },
    { id: "scopes", label: barLabel("scopes", null), labelText: barLabel("scopes", null) },
    { id: "multibyte", label: barLabel("multibyte", null), labelText: barLabel("multibyte", null) },
  ];
  if (topTag) {
    const topPicker = (
      <select
        value={topTag}
        aria-label={AUDIT_TABLE_TOP_SCOPE_PICKER_LABEL}
        onChange={(e) => setTopPick(e.target.value)}
      >
        {(tokens.includes(topTag) ? tokens : [topTag, ...tokens]).map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
    );
    bars.push({
      id: "top",
      labelText: barLabel("top", topTag),
      picker: topPicker,
      label: barLabel("top", topTag),
    });
  }
  bars.push({ id: "clock", label: barLabel("clock", null), labelText: barLabel("clock", null) });
  const chips = filterChips(
    filters,
    {
      scope: AUDIT_TABLE_FILTER_REGION_LABEL, foot: AUDIT_TABLE_FILTER_FOOTPRINT_LABEL, path: AUDIT_TABLE_FILTER_PATH_LABEL, multi: AUDIT_TABLE_FILTER_MULTI_LABEL,
      dup: AUDIT_TABLE_FILTER_DUPLICATE_LABEL, clock: AUDIT_TABLE_FILTER_CLOCK_LABEL, adv: AUDIT_TABLE_FILTER_ADVERT_LABEL,
      loop: AUDIT_TABLE_FILTER_LOOP_LABEL, pkts: AUDIT_TABLE_FILTER_PKTS_LABEL,
    },
    settings, topTag, tokens,
  );

  const nActive = activeFilterCount(filters, location, role) + (findingLevel ? 1 : 0);
  const locRole = (
    <>
        <FilterSelect id="aud-location" label={AUDIT_TABLE_LOCATION_FILTER_LABEL} value={location} onChange={setLocation}>
          <option value="">{AUDIT_TABLE_LOCATION_FILTER_ALL_LABEL}</option>
          <Options options={locations} />
        </FilterSelect>
        <FilterSelect id="aud-role" label={AUDIT_TABLE_ROLE_FILTER_LABEL} value={role} onChange={setRole}>
          <option value="">{AUDIT_TABLE_ROLE_FILTER_ALL_LABEL}</option>
          {roles.map((r) => <option key={r} value={r}>{auditRoleLabel(r)}</option>)}
        </FilterSelect>
    </>
  );

  return (
    <div>
      <div className="node-list-toolbar audit-filter-row">
        <div className="theme-toggle audit-tab-row" role="group" aria-label="Window">
          {(["24h", "7d"] as AuditWindow[]).map((w) => (
            <button key={w} data-active={windowKey === w} disabled={!!takenAt && w !== "7d"} onClick={() => setWindowKey(w)}>{w}</button>
          ))}
        </div>
        <AuditHistorySelect />
        {isPhone && (
          <button type="button" className="rf-btn rf-btn-outline" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((o) => !o)}>
            {nActive > 0 ? `${AUDIT_TABLE_FILTERS_BUTTON_LABEL} (${nActive})` : AUDIT_TABLE_FILTERS_BUTTON_LABEL}
          </button>
        )}
        {!isPhone && locRole}
      </div>

      {(!isPhone || filtersOpen) && (
        <div className={isPhone ? "node-list-toolbar audit-filter-row audit-filter-panel" : "node-list-toolbar audit-filter-row"}>
          {isPhone && locRole}
        <FilterSelect id="aud-f-scope" label={AUDIT_TABLE_FILTER_REGION_LABEL} value={filters.scope ?? ""} onChange={(v) => setFilter("scope", v)}>
          <Options options={scopeOpts.main} />
          {scopeOpts.tokens.length > 0 && (
            <optgroup label={AUDIT_TABLE_HAS_SCOPE_GROUP_LABEL}><Options options={scopeOpts.tokens} /></optgroup>
          )}
        </FilterSelect>
        <FilterSelect id="aud-f-foot" label={AUDIT_TABLE_FILTER_FOOTPRINT_LABEL} value={filters.foot ?? ""} onChange={(v) => setFilter("foot", v)}>
          <Options options={FOOTPRINT_FILTER_OPTIONS} />
        </FilterSelect>
        <FilterSelect id="aud-f-path" label={AUDIT_TABLE_FILTER_PATH_LABEL} value={filters.path ?? ""} onChange={(v) => setFilter("path", v)}>
          <Options options={PATH_FILTER_OPTIONS} />
        </FilterSelect>
        <FilterSelect id="aud-f-multi" label={AUDIT_TABLE_FILTER_MULTI_LABEL} value={filters.multi ?? ""} onChange={(v) => setFilter("multi", v)}>
          <Options options={MULTI_FILTER_OPTIONS} />
        </FilterSelect>
        <FilterSelect id="aud-f-dup" label={AUDIT_TABLE_FILTER_DUPLICATE_LABEL} value={filters.dup ?? ""} onChange={(v) => setFilter("dup", v)}>
          <Options options={DUP_FILTER_OPTIONS} />
        </FilterSelect>
        <FilterSelect id="aud-f-clock" label={AUDIT_TABLE_FILTER_CLOCK_LABEL} value={filters.clock ?? ""} onChange={(v) => setFilter("clock", v)}>
          <Options options={clockFilterOptions(settings)} />
        </FilterSelect>
        <FilterSelect id="aud-f-adv" label={AUDIT_TABLE_FILTER_ADVERT_LABEL} value={filters.adv ?? ""} onChange={(v) => setFilter("adv", v)}>
          <Options options={advertFilterOptions(settings)} />
        </FilterSelect>
        <FilterSelect id="aud-f-loop" label={AUDIT_TABLE_FILTER_LOOP_LABEL} value={filters.loop ?? ""} onChange={(v) => setFilter("loop", v)}>
          <Options options={LOOP_FILTER_OPTIONS} />
        </FilterSelect>
        <FilterSelect id="aud-f-pkts" label={AUDIT_TABLE_FILTER_PKTS_LABEL} value={filters.pkts ?? ""} onChange={(v) => setFilter("pkts", v)}>
          <Options options={pktsFilterOptions(settings)} />
        </FilterSelect>
        <button type="button" className="rf-btn rf-btn-outline" onClick={() => { setFilters({}); setFindingLevel(null); }}>{AUDIT_TABLE_CLEAR_FILTERS_LABEL}</button>
      </div>
      )}

      <div className="audit-bars">
        <div className="audit-metrics-head">
          <div className="page-section-title" style={{ fontSize: 11 }}>{AUDIT_TABLE_BARS_HEADING}</div>
          <div className="audit-metrics-head-right">
            {bars.find((b) => b.id === "top")?.picker && (
              <span className="audit-metric-picker">Top-Level Region {bars.find((b) => b.id === "top")?.picker}</span>
            )}
            <div className="theme-toggle" role="group" aria-label="Metrics view">
              {AUDIT_METRICS_VIEWS.map((v) => (
                <button key={v.id} type="button" data-active={metricsView === v.id} onClick={() => chooseMetricsView(v.id)}>{v.label}</button>
              ))}
            </div>
          </div>
        </div>
        {metricsView === "bars" && bars.map((b) => {
          const counts = barCounts(barRows[b.id], b.id, topTag);
          return (
            <SummaryBar
              key={b.id}
              bar={b.id}
              label={b.label}
              counts={counts}
              tooltip={barTooltip(b.id, counts, topTag)}
              selected={barSelection(b.id, filters)}
              onPick={(s) => pickSegment(b.id, s)}
              effectiveTheme={effectiveTheme}
              packets={b.id === "path" ? data?.path_hash_packets : undefined}
              filtered={filteredNow || !!location || !!role}
            />
          );
        })}
        {metricsView !== "bars" && (() => {
          const items: AuditMetricItem[] = bars.map((b) => {
            const counts = barCounts(barRows[b.id], b.id, topTag);
            return { id: b.id, labelText: b.labelText, counts, tooltip: barTooltip(b.id, counts, topTag), selected: barSelection(b.id, filters), scopeTag: b.id === "top" ? topTag : null };
          });
          const View = metricsView === "gauges" ? AuditGauges : AuditRings;
          return <View items={items} onPick={pickSegment} effectiveTheme={effectiveTheme} />;
        })()}
      </div>

      <div className="audit-filter-row">
        <div className="audit-level-worklist" role="group" aria-label="Findings worklist">
          <span className="audit-level-worklist-label">Finding levels</span>
          {currentFindingLevels && levelCounts ? (
            <>
              {[...AUDIT_LEVEL_ORDER.filter((level) => level !== "not_enough_data"), UNKNOWN_FINDING_LEVEL].map((level) => {
                const unknown = level === UNKNOWN_FINDING_LEVEL;
                const label = findingLevelLabel(level);
                const count = levelCounts[level] ?? 0;
                const title = unknown
                  ? "Distinct matching nodes with not enough data, a missing level, or an unrecognized level. No finding is not counted here, and this bucket does not mean healthy."
                  : `Distinct matching nodes with at least one ${label} finding. Counts can overlap because one node can have findings at multiple levels. Selecting this count shows matching nodes under the current role, location, and column filters.`;
                return (
                  <button
                    key={level}
                    type="button"
                    className={`audit-level-chip${unknown ? " audit-level-chip-unknown" : ""}`}
                    aria-pressed={findingLevel === level}
                    title={title}
                    onClick={() => setFindingLevel((active) => active === level ? null : level)}
                  >
                    <span>{label}</span><strong>{count}</strong>
                  </button>
                );
              })}
            </>
          ) : worklistUnavailable ? (
            <span className="audit-level-unavailable">
              Finding levels unavailable; the node list is not classified as healthy.{" "}
              <button type="button" className="audit-level-retry" onClick={() => setFindingRefreshNonce((n) => n + 1)}>Retry</button>
            </span>
          ) : findingsLineageMissing ? (
            <span className="audit-level-unavailable">Finding levels will be available after the next Audit report refresh.</span>
          ) : (
            <span className="audit-level-unavailable">Loading finding levels…</span>
          )}
        </div>
      </div>

      <div className="audit-filter-row">
        <label>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          {" "}{AUDIT_TABLE_SHOW_ALL_LABEL}
        </label>
        <span className="node-detail-note">
          {findingLevel
            ? (currentFindingLevels
              ? `${shown.length} ${shown.length === 1 ? "node" : "nodes"} with ${findingLevelLabel(findingLevel)} findings under the current filters.`
              : (findingsLineageMissing
                ? "Finding levels will be available after the next Audit report refresh."
                : (worklistUnavailable ? "Finding worklist unavailable." : "Refreshing finding worklist…")))
            : auditCountLine({ shown: shown.length, total: population.length, issues, filtered: filteredNow, showAll, role })}
        </span>
        {chips.map((c) => (
          <span className="audit-chip" key={c.filter}>
            {c.text}
            <button type="button" aria-label={AUDIT_TABLE_CLEAR_FILTERS_LABEL} onClick={() => setFilter(c.filter, "")}>✕</button>
          </span>
        ))}
        {findingLevel && (
          <span className="audit-chip" key="finding-level-filter">
            {`Finding: ${findingLevelLabel(findingLevel)}`}
            <button type="button" aria-label="Clear finding level filter" onClick={() => setFindingLevel(null)}>✕</button>
          </span>
        )}
      </div>

      {isPhone && (
        <div className="audit-sort-row">
          <label>
            {AUDIT_TABLE_SORT_LABEL}{" "}
            <select value={sortChoiceValue(sortKey)} onChange={(e) => { setSortKey(parseSortChoice(e.target.value)); setSortDir(1); }}>
              <option value="">{AUDIT_TABLE_SORT_DEFAULT_LABEL}</option>
              {SORT_COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </label>
          <button type="button" disabled={sortKey === null} onClick={() => setSortDir((d) => (d === 1 ? -1 : 1))}>
            {sortDir === 1 ? AUDIT_TABLE_SORT_ASC_LABEL : AUDIT_TABLE_SORT_DESC_LABEL}
          </button>
        </div>
      )}
      {isPhone && (
        <div className="audit-card-list">
          {slice.length === 0 && <div className="settings-field-desc" style={{ padding: 16 }}>{findingLevel && !currentFindingLevels ? (findingsLineageMissing ? "Finding levels will be available after the next Audit report refresh." : (worklistUnavailable ? "Finding worklist unavailable." : "Refreshing finding worklist…")) : AUDIT_TABLE_NO_MATCH_LABEL}</div>}
          {slice.map((row) => (
            <AuditCard
              key={row.native_id}
              row={row}
              effectiveTheme={effectiveTheme}
              settings={settings}
              expanded={expandedId === row.native_id}
              onToggle={() => setExpandedId((id) => (id === row.native_id ? null : row.native_id))}
              windowKey={windowKey}
              observers={observers}
              widthLookbackDays={data?.width_lookback_days ?? 7}
            />
          ))}
        </div>
      )}

      {!isPhone && (
      <div className="audit-table-wrap" data-more-right={moreRight ? "true" : "false"}>
      <div className="audit-table-scroll" ref={scrollRef}>
        <table className="node-list-table audit-table">
          <thead>
            <tr>
              {SORT_COLUMNS.map((c, i) => (
                <th
                  key={c.key}
                  className={i === 0 ? "audit-table-node-col" : c.key === "region" ? "audit-table-region-col" : c.key === "role" ? "audit-col-role" : undefined}
                  onClick={() => toggleSort(c.key)}
                  aria-sort={sortKey === c.key ? (sortDir === 1 ? "ascending" : "descending") : "none"}
                >
                  <HoverTip placement="above" text={headerTooltip(c.key, settings, lookbackDays, data?.width_lookback_days ?? 7)}>
                    {c.label}
                    {sortKey === c.key ? (sortDir === 1 ? " ▲" : " ▼") : sortKey === null && c.key === "node" ? <span className="audit-sort-default"> ▲</span> : ""}
                  </HoverTip>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.length === 0 && (
              <tr><td colSpan={SORT_COLUMNS.length} className="settings-field-desc" style={{ padding: 16 }}>{findingLevel && !currentFindingLevels ? (findingsLineageMissing ? "Finding levels will be available after the next Audit report refresh." : (worklistUnavailable ? "Finding worklist unavailable." : "Refreshing finding worklist…")) : AUDIT_TABLE_NO_MATCH_LABEL}</td></tr>
            )}
            {slice.map((row) => (
              <Fragment key={row.native_id}>
                <tr className="node-list-row" data-active={expandedId === row.native_id ? "true" : "false"} onClick={() => setExpandedId((id) => (id === row.native_id ? null : row.native_id))}>
                  <td className="audit-table-node-col behaviour-node-cell">
                    <a href={`#/nodes/${encodeURIComponent(row.native_id)}`} className={`node-name-link${/\S{24,}/.test(row.name) ? " audit-name-unbroken" : ""}`} onClick={(e) => e.stopPropagation()}>
                      {row.name}
                    </a>
                    <div className="behaviour-node-short-id settings-field-desc">
                      {row.short_id}{row.region.label ? ` · ${row.region.label}` : ""}
                    </div>
                    <div className="audit-role-under settings-field-desc">{auditRoleLabel(row.role)}</div>
                  </td>
                  {cardFields().map((f) => (
                    <td key={f.key} className={f.key === "region" ? "audit-table-region-col" : f.key === "role" ? "audit-col-role" : undefined}>
                      <AuditFieldValue colKey={f.key} row={row} effectiveTheme={effectiveTheme} settings={settings} />
                    </td>
                  ))}
                </tr>
                {expandedId === row.native_id && (
                  <tr>
                    <td colSpan={SORT_COLUMNS.length} className="behaviour-node-detail-cell audit-expand-cell">
                      <div className="audit-expand-panel" style={panelW ? { width: panelW } : undefined}>
                        <EvidenceBlock row={row} windowKey={windowKey} observers={observers} widthLookbackDays={data?.width_lookback_days ?? 7} effectiveTheme={effectiveTheme} />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      </div>
      )}

      <div className="audit-filter-row" style={{ marginTop: 8 }}>
        <button type="button" disabled={pageIndex === 0} onClick={() => setPage(pageIndex - 1)}>{AUDIT_TABLE_PREV_LABEL}</button>
        <button type="button" disabled={pageIndex >= pages - 1} onClick={() => setPage(pageIndex + 1)}>{AUDIT_TABLE_NEXT_LABEL}</button>
        <span className="node-detail-note">
          {auditTablePagerLine(shown.length ? pageIndex * PAGE_SIZE + 1 : 0, Math.min(shown.length, (pageIndex + 1) * PAGE_SIZE), shown.length)}
        </span>
      </div>
    </div>
  );
}
